import { createFileRoute, Outlet, redirect, useMatches, useNavigate } from "@tanstack/react-router";
import { DashboardLayout } from "@/components/dashboard-layout";
import { deleteOrganisationFn, organisationsQueryOptions } from "@/server/organisations";
import { Card, CardContent } from "@/components/ui/card";
import { useMemo, useState, useCallback } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CrudEntry } from "@/lib/mma/types.gen";
import { ContextUser, checkIfUserMayWrite, checkIfUserMayDelete, getPermissionRestraintMessage } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/organisations")({
  component: OrganisationRouteComponent,
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;

    return { title: "Organisations" };
  },
  loader: async ({ context }) => {
    return {
      user: context.user as ContextUser | null | undefined,
      crumb: "Organisations",
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function OrganisationRouteComponent() {
  const navigate = useNavigate();
  const matches = useMatches();
  const hasChildRoute = matches[matches.length - 1].fullPath !== "/organisations";
  const { userProfileId, accessControl, user } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "ORGANISATION" });
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "ORGANISATION");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "ORGANISATION");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const organisationsQueryOptionsMemo = useMemo(() => organisationsQueryOptions(), []);
  const organisationsQuery = useQuery(organisationsQueryOptionsMemo);
  const organisations = organisationsQuery.data ?? [];

  const handleDeleteClick = useCallback((organisationId: string, organisationName: string) => {
    setSelectedOrganisation({ id: organisationId, name: organisationName });
    setDialogOpen(true);
  }, []);

  const submenuOnClick = useCallback(
    (item: any, functionType: string) => {
      if (functionType === "delete") {
        if (!mayDelete) {
          toast.error(getPermissionRestraintMessage("delete organisations"));
          return;
        }
        handleDeleteClick(item.parent.id, item.parent.name);
      }
    },
    [handleDeleteClick, mayDelete]
  );

  const submenu = useMemo(
    () => ({
      items: organisations,
      isLoading: organisationsQuery.isLoading,
      title: "Organisations",
      active: true,
      createNew: mayWrite ? [{ label: "+ Create new organisation", href: "/organisations/new-org" }] : undefined,
      onClick: submenuOnClick,
    }),
    [organisations, organisationsQuery.isLoading, submenuOnClick, mayWrite]
  );

  const deleteOrganisationMutation = useMutation({
    mutationFn: deleteOrganisationFn,
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      navigate({ to: "/organisations" });
    },
  });

  async function handleConfirmDelete() {
    if (!selectedOrganisation) return;
    setLoading(true);
    const deleteOrganisationPromise = deleteOrganisationMutation.mutateAsync({
      data: { orgId: Number(selectedOrganisation.id) },
    });
    toast.promise(deleteOrganisationPromise, {
      loading: "Deleting organisation...",
      success: () => {
        return "Organisation deleted successfully";
      },
      error: "Failed to delete organisation",
    });
    setLoading(false);
    setDialogOpen(false);
    setSelectedOrganisation(null);
  }

  return (
    <>
      <DashboardLayout submenu={submenu}>
        {!hasChildRoute ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select an Organisation from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete organisation"
        description={
          selectedOrganisation
            ? `Are you sure you want to delete the organisation "${selectedOrganisation.name}"?
          All Users, Settings, Divergence reports, and other related information will be removed.
`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedOrganisation(null);
        }}
        loading={loading}
      />
    </>
  );
}
